import React, { useEffect, useState } from 'react';
import { Link, useLocation, Routes, Route, useNavigate } from 'react-router-dom';
import './Dashboard.css';
import DashboardHome from './DashboardHome';
import QuotationCreationPage from './QuotationCreationPage';
import QuotationList from './QuotationList';
import BannerManagement from './BannerManagement';
import BackupManagement from './BackupManagement';
import CarsManagement from './CarsManagement';
import CRMDashboard from './crm/CRMDashboard';
import CRMPortal from './crm/CRMPortal';
import apiService from '../../services/apiService';

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState<string>('Admin'); // Default to Admin if not found
  const [userName, setUserName] = useState<string>('Admin');
  const [availability, setAvailability] = useState<string>('Available');
  const [isIdle, setIsIdle] = useState(false);
  const availabilityRef = React.useRef(availability);
  
  // Sidebar toggle state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Sync ref with current availability state
  useEffect(() => {
    availabilityRef.current = availability;
  }, [availability]);

  // Auto-collapse sidebar when entering CRM to maximize space
  useEffect(() => {
    if (location.pathname.includes('/crm')) {
      setIsSidebarCollapsed(true);
    } else if (window.innerWidth > 768) {
      setIsSidebarCollapsed(false);
    }
  }, [location.pathname]);

  const handleAvailabilityChange = async (newStatus: string) => {
    setAvailability(newStatus);
    availabilityRef.current = newStatus;
    const userStr = sessionStorage.getItem('aswCrmUser') || sessionStorage.getItem('aswUser') || localStorage.getItem('aswCrmUser') || localStorage.getItem('aswUser');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        const res = await apiService.updateCrmUser(user.id, { availability_status: newStatus });
        if (res.success) {
          user.availability_status = newStatus;
          sessionStorage.setItem('aswCrmUser', JSON.stringify(user));
          sessionStorage.setItem('aswUser', JSON.stringify(user));
          localStorage.setItem('aswCrmUser', JSON.stringify(user));
          localStorage.setItem('aswUser', JSON.stringify(user));
        }
      } catch (err) {
        console.error('Failed to update status', err);
      }
    }
  };

  useEffect(() => {
    // Get user info from storage (sessionStorage first)
    const userStr = sessionStorage.getItem('aswCrmUser') || sessionStorage.getItem('aswUser') || localStorage.getItem('aswCrmUser') || localStorage.getItem('aswUser');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.role) setUserRole(user.role);
        if (user.name) setUserName(user.name);
        if (user.availability_status) {
          setAvailability(user.availability_status);
          availabilityRef.current = user.availability_status;
        }
        
        // Setup background ping
        if (user.id) {
          apiService.pingActivity(user.id).then(res => {
            if (res && res.availabilityStatus) {
              setAvailability(res.availabilityStatus);
              availabilityRef.current = res.availabilityStatus;
              user.availability_status = res.availabilityStatus;
              localStorage.setItem('aswCrmUser', JSON.stringify(user));
            }
          }); // initial ping
          const interval = setInterval(() => {
            if (!document.hidden) {
              apiService.pingActivity(user.id).then(res => {
                if (res && res.availabilityStatus) {
                  setAvailability(res.availabilityStatus);
                  availabilityRef.current = res.availabilityStatus;
                  const current = JSON.parse(localStorage.getItem('aswCrmUser') || '{}');
                  if (current.id) {
                    current.availability_status = res.availabilityStatus;
                    localStorage.setItem('aswCrmUser', JSON.stringify(current));
                  }
                }
              });
            }
          }, 3 * 60 * 1000); // exactly 3 mins
          return () => clearInterval(interval);
        }
      } catch (e) {
        console.error('Failed to parse user info', e);
      }
    }
  }, []);

  // Idle tracking logic
  useEffect(() => {
    if (userRole !== 'Sales Team') return; // only monitor sales executive inactivity
    
    const userStr = localStorage.getItem('aswCrmUser') || localStorage.getItem('aswUser');
    if (!userStr) return;
    let userId: string;
    try {
      userId = JSON.parse(userStr).id;
    } catch {
      return;
    }
    if (!userId) return;

    let idleTimeout: NodeJS.Timeout;

    const resetIdleTimeout = () => {
      clearTimeout(idleTimeout);
      
      setIsIdle(prevIdle => {
        if (prevIdle) {
          console.log('User active again. Marking Available.');
          handleAvailabilityChange('Available');
        }
        return false;
      });

      idleTimeout = setTimeout(() => {
        if (availabilityRef.current === 'Available') {
          console.log('User idle for 5 minutes. Marking Offline.');
          setIsIdle(true);
          handleAvailabilityChange('Offline');
        }
      }, 5 * 60 * 1000);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    const handleEvent = () => resetIdleTimeout();
    
    events.forEach(event => {
      window.addEventListener(event, handleEvent);
    });

    resetIdleTimeout();

    return () => {
      clearTimeout(idleTimeout);
      events.forEach(event => {
        window.removeEventListener(event, handleEvent);
      });
    };
  }, [userRole]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Available': return '#10B981';
      case 'Busy': return '#EF4444';
      case 'Break': return '#F59E0B';
      case 'Offline': return '#6B7280';
      default: return '#10B981';
    }
  };

  const isActive = (path: string) => {
    return location.pathname.startsWith(path) && (path !== '/dashboard' || location.pathname === '/dashboard') ? 'active' : '';
  };

  const handleLogout = async () => {
    try {
      console.log('Logging out...');
      const userStr = localStorage.getItem('aswCrmUser') || localStorage.getItem('aswUser');
      let userId = null;
      if (userStr) {
        userId = JSON.parse(userStr).id;
      }
      await apiService.logout(userId);
      console.log('Logout API call successful');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('aswSessionToken');
      localStorage.removeItem('aswCrmToken');
      localStorage.removeItem('aswUser');
      localStorage.removeItem('aswCrmUser');
      console.log('Cleared session data, redirecting to login...');
      navigate('/login');
    }
  };

  // Check roles for permissions
  const isAdmin = userRole === 'Admin';
  // If we have a non-admin role, they might only see CRM or subset of features.
  // We can adjust this to show/hide cars/quotations. For now, CRM gets a prominent tab.

  return (
    <div className="dashboard-layout">
      <div className={`dashboard-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="dashboard-logo">
          <h2>{!isSidebarCollapsed ? 'ASW Admin' : 'ASW'}</h2>
        </div>
        <nav className="dashboard-nav">
          <Link to="/dashboard" className={`nav-item ${isActive('/dashboard')}`}>
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1"></rect>
                <rect x="14" y="3" width="7" height="7" rx="1"></rect>
                <rect x="14" y="14" width="7" height="7" rx="1"></rect>
                <rect x="3" y="14" width="7" height="7" rx="1"></rect>
              </svg>
            </span>
            <span>Dashboard</span>
          </Link>
          
          {(isAdmin || userRole === 'Sales Team') && (
            <>
              <Link to="/dashboard/quotation" className={`nav-item ${isActive('/dashboard/quotation')}`}>
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="12" y1="18" x2="12" y2="12"></line>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                </span>
                <span>Create Quotation</span>
              </Link>
              <Link to="/dashboard/quotations" className={`nav-item ${isActive('/dashboard/quotations')}`}>
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <line x1="10" y1="9" x2="8" y2="9"></line>
                  </svg>
                </span>
                <span>Manage Quotations</span>
              </Link>
              <Link to="/dashboard/cars" className={`nav-item ${isActive('/dashboard/cars')}`}>
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9C2.1 11 2 11.5 2 12v4c0 .6.4 1 1 1h2"></path>
                    <circle cx="7" cy="17" r="2"></circle>
                    <path d="M9 17h6"></path>
                    <circle cx="17" cy="17" r="2"></circle>
                  </svg>
                </span>
                <span>Cars</span>
              </Link>
            </>
          )}

          <Link to="/crm" className={`nav-item ${isActive('/crm')}`}>
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </span>
            <span>CRM Portal</span>
          </Link>

          {isAdmin && (
            <>
              <Link to="/dashboard/banners" className={`nav-item ${isActive('/dashboard/banners')}`}>
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                </span>
                <span>Banner Management</span>
              </Link>

              <Link to="/dashboard/backups" className={`nav-item ${isActive('/dashboard/backups')}`}>
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                </span>
                <span>Daily Backups</span>
              </Link>
            </>
          )}

          <Link to="/" className="nav-item">
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
            </span>
            <span>Back to Site</span>
          </Link>
        </nav>
      </div>
      
      <div className={`dashboard-content ${isSidebarCollapsed ? 'expanded' : ''}`}>
        <div className="dashboard-header">
          <div className="header-left">
            <button 
              className="sidebar-toggle-btn"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          </div>
          <div className="user-profile">
            {userRole === 'Sales Team' && (
              <div className="availability-status-selector" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '16px' }}>
                <span className="status-dot" style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  marginRight: '6px', 
                  backgroundColor: getStatusColor(availability),
                  boxShadow: '0 0 4px ' + getStatusColor(availability)
                }}></span>
                <select 
                  value={availability} 
                  onChange={(e) => handleAvailabilityChange(e.target.value)}
                  style={{ 
                    padding: '3px 6px', 
                    borderRadius: '4px', 
                    border: '1px solid var(--crm-border, #e2e8f0)', 
                    backgroundColor: 'var(--crm-bg-card, #f8fafc)', 
                    color: 'var(--crm-text-primary, #0f172a)',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  <option value="Available">Available</option>
                  <option value="Busy">Busy</option>
                  <option value="Break">Break</option>
                  <option value="Offline">Offline</option>
                </select>
              </div>
            )}
            <span className="user-avatar" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '50%', background: '#e2e8f0', color: '#475569', marginRight: '6px' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </span>
            <span style={{ marginRight: '12px', fontSize: '0.85rem', fontWeight: 500 }}>{userName} ({userRole})</span>
            <button className="logout-button" onClick={handleLogout}>
              <span className="logout-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
              </span>
              Logout
            </button>
          </div>
        </div>
        <div className="dashboard-main-content">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/quotation" element={<QuotationCreationPage />} />
            <Route path="/quotations" element={<QuotationList />} />
            <Route path="/quotation/edit/:id" element={<QuotationCreationPage />} />
            <Route path="/cars" element={<CarsManagement />} />
            <Route path="/banners" element={<BannerManagement />} />
            <Route path="/backups" element={<BackupManagement />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout; 